import React from "react";
import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";

import { MeetingElog, ScheduledMeeting } from "@services/meetings/types";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { getMeetingElog, putMeetingElog } from "@services/meetings";
import { postMonitoringMeeting } from "@services/monitoring";
import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import Button from "@common/components/Button";

import { colorCorrectHTML } from "../common/color";

import { Loader } from "./SkeletonLoader";

import CommentTextSection from "@common/components/CommentTextSection";
import { isRoomSweepMeeting } from "@common/utils/meetings";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  canEditElogsAndRevisions,
  canViewElogsAndRevisions,
} from "@common/utils/user";

export const Comments = () => {
  const { t } = useTranslation();
  const context = React.useContext(RoomsRealtimeContext);
  const { dispatchNotification } = React.useContext(NotificationContext);
  const [user] = useAuthenticatedUser();
  const canViewElog = canViewElogsAndRevisions(user);
  const canEditElog = canEditElogsAndRevisions(user);
  const refetchMeeting = context.detail?.refetch;
  const meeting = { ...context.detail?.meeting };
  const dispatchError = useErrorNotification();
  const [isEdit, setIsEdit] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [elog, setElog] = React.useState<MeetingElog>();
  const [notes, setNotes] = React.useState({
    meeting: "",
    meetingNoteEdited: false,
    producer: "",
    producerNoteEdited: false,
  });

  const refresh = async () => {
    if (context?.detail?.meeting?.id) {
      try {
        setLoading(true);
        if (!isRoomSweepMeeting(context.detail.meeting) && canViewElog) {
          const elog = await getMeetingElog(context.detail?.meeting?.id);
          setElog(elog);
          setNotes({
            meeting: meeting?.notes ?? meeting?.scheduledMeeting?.notes ?? "",
            producer: elog?.note ?? "",
            meetingNoteEdited: false,
            producerNoteEdited: false,
          });
        } else {
          setNotes({
            meeting: meeting?.notes ?? meeting?.scheduledMeeting?.notes ?? "",
            producer: "",
            meetingNoteEdited: false,
            producerNoteEdited: false,
          });
        }
      } catch (e) {
        dispatchError(e);
      } finally {
        setLoading(false);
      }
    }
  };

  React.useEffect(() => {
    refresh();
  }, [context?.detail?.meeting?.id]);

  const handleChangeEditMode = () => setIsEdit(!isEdit);

  const handleChangeNotes = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newState = { ...notes, [event.target.name]: event.target.value };

    switch (event.target.name) {
      case "meeting":
        newState = { ...newState, meetingNoteEdited: true };
        break;
      case "producer":
        newState = { ...newState, producerNoteEdited: true };
        break;
    }

    setNotes(newState);
  };

  const handleSave = async () => {
    try {
      const meetingPromises = [];
      if (notes.meetingNoteEdited) {
        meetingPromises.push(
          postMonitoringMeeting(
            meeting?.id as number,
            {
              ...meeting,
              notes: notes.meeting,
            } as ScheduledMeeting
          )
        );
      }

      if (notes.producerNoteEdited && canEditElog) {
        meetingPromises.push(
          putMeetingElog(meeting?.id, {
            ...elog,
            note: notes.producer,
          } as MeetingElog)
        );
      }

      await Promise.all(meetingPromises);
      dispatchNotification({
        title: t("Success"),
        message: t("Meeting note has been successfully updated."),
        severity: "success",
      });
    } catch (e) {
      dispatchError(e);
    } finally {
      handleChangeEditMode();
      refetchMeeting && refetchMeeting();
    }
  };

  const handleCancel = () => {
    setNotes({
      meeting: meeting?.notes ?? "",
      meetingNoteEdited: false,
      producer: elog?.note ?? "",
      producerNoteEdited: false,
    });
    handleChangeEditMode();
  };

  return (
    <>
      {loading || (!elog && !isRoomSweepMeeting(context?.detail?.meeting)) ? (
        <Loader />
      ) : (
        <>
          <CommentTextSection
            isEdit={isEdit}
            content={colorCorrectHTML(notes.meeting)}
            label="Meeting notes"
            name="meeting"
            onChange={handleChangeNotes}
          />
          {!isRoomSweepMeeting(context?.detail?.meeting) && (
            <CommentTextSection
              isEdit={isEdit}
              content={notes.producer}
              label="Producer notes"
              name="producer"
              onChange={handleChangeNotes}
            />
          )}
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "flex-end"
            }}>
            {isEdit ? (
              <>
                <Button
                  variant="contained"
                  color="white"
                  size="small"
                  disabled={
                    !notes.meetingNoteEdited && !notes.producerNoteEdited
                  }
                  onClick={handleSave}
                >
                  {t("Save")}
                </Button>
                <Button
                  variant="outlined"
                  color="greyed"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={handleCancel}
                >
                  {t("Cancel")}
                </Button>
              </>
            ) : (
              <Button
                color="white"
                variant="contained"
                size="small"
                onClick={handleChangeEditMode}
              >
                {t("Edit")}
              </Button>
            )}
          </Stack>
        </>
      )}
    </>
  );
};

export default Comments;
